
import { useCallback, useEffect, useState, Suspense, useRef, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Breadcrumbs from './components/common/Breadcrumbs';
import DeviceDropdown from './components/dashboard/DeviceDropdown';
import IntervalButtons from './components/dashboard/IntervalButtons';
import DevicePlot from './components/dashboard/DevicePlot';
import TabularData from './components/dashboard/TabularData';
import WanDashboard from './components/wan/WanDashboard';
import AlertsDashboard from './components/alerts/AlertsDashboard';
import CrashDashboard from './components/crash/CrashDashboard';
import RestartsDashboard from './components/restarts/RestartsDashboard';
import StatusDashboard from './components/status/StatusDashboard';
import ErrorMessage from './components/common/ErrorMessage';
import { ToastProvider, useToast } from './components/common/ToastProvider';
import { useWifiData } from './hooks/useWifiData';
import { useReportReasons } from './hooks/useReportReasons';
import { useUserIssueReports } from './hooks/useUserIssueReports';
import { PAGES, VIEWS, USER_ROLES, INTERVALS } from './utils/constants';
import './styles/common.css';
import './styles/navigation.css';
import './styles/dashboard.css';
import './styles/components.css';
import { PgUsersDashboard } from './components/users/PgUsersDashboard';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { RequireAuth } from './auth/RequireAuth';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { SettingsIcon } from './components/common/Icons';
import { IssueReportsList } from './components/home/IssueReportsList';
import { ReportIssueModal } from './components/home/ReportIssueModal';
import { SwitchRoleModal } from './components/auth/SwitchRoleModal';
import { apiClient } from './utils/api';
import ThemeToggle from './components/common/ThemeToggle';
import clientPackage from '../package.json';
import { FCMManager } from './pages/FCMManager';
import { UserPreferences } from './pages/UserPreferences';
import { AppMetrics } from './pages/AppMetrics';
import NetworkDiagram from './pages/NetworkDiagram';
import SetupWizardPage from './pages/SetupWizardPage';
import MonitorPage from './pages/MonitorPage';
import ChUtilPage from './pages/ChUtilPage';
import TxRxRetriesPage from './pages/TxRxRetriesPage';
import AgentChat from './components/agent/AgentChat';
import AgentInbox from './components/inbox/AgentInbox';

function DashboardShell() {
    const navigate = useNavigate();
    const location = useLocation();
    const [currentPage, setCurrentPage] = useState(() => {
        const path = location.pathname.endsWith('/') && location.pathname !== '/'
            ? location.pathname.slice(0, -1)
            : location.pathname;

        switch (path) {
            case '/wan':
                return PAGES.WAN;
            case '/wifi':
                return PAGES.WIFI;
            case '/wifi/ch-util':
                return PAGES.CH_UTIL;
            case '/wifi/tx-rx-retries':
                return PAGES.TX_RX_RETRIES;
            case '/app-metrics':
                return PAGES.APP_METRICS;
            case '/users':
                return PAGES.USERS;
            case '/fcm-manager':
                return PAGES.FCM_MANAGER;
            case '/user-preferences':
                return PAGES.USER_PREFERENCES;
            case '/preferences':
                return PAGES.PREFERENCES;
            case '/alerts':
                return PAGES.ALERTS;
            case '/crash-report':
                return PAGES.CRASH_REPORT;
            case '/restarts':
                return PAGES.RESTARTS;
            case '/status':
                return PAGES.STATUS;
            case '/network-map':
                return PAGES.NETWORK_DIAGRAM;
            case '/setup-wizard':
                return PAGES.SETUP_WIZARD;
            case '/monitor':
                return PAGES.MONITOR;
            case '/diagnostics-ai':
                return PAGES.AI_AGENT;
            case '/agent-inbox':
                return PAGES.AGENT_INBOX;
            default:
                return PAGES.HOME;
        }
    });
    const [selectedDevice, setSelectedDevice] = useState('');
    const [selectedInterval, setSelectedInterval] = useState('today');
    const [customRange, setCustomRange] = useState({ start: '', end: '' });
    const [currentView, setCurrentView] = useState(VIEWS.PLOTS);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const alertsMenuRef = useRef(null);
    const usersMenuRef = useRef(null);
    const wifiMenuRef = useRef(null);
    const networkMenuRef = useRef(null);
    const [alertsMenuOpen, setAlertsMenuOpen] = useState(false);
    const [usersMenuOpen, setUsersMenuOpen] = useState(false);
    const [wifiMenuOpen, setWifiMenuOpen] = useState(false);
    const [networkMenuOpen, setNetworkMenuOpen] = useState(false);
    const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
    const { user, logout, hasPermission, impersonateRole, revertRole, isImpersonating, refreshTokens } = useAuth();

    // Proactive Token Refresh
    useEffect(() => {
        if (!user) return; // Only run if logged in

        const REFRESH_INTERVAL = 4 * 60 * 1000; // 4 minutes
        const intervalId = setInterval(async () => {
            try {
                await refreshTokens();
                // console.debug('Proactive token refresh successful');
            } catch (error) {
                console.error('Proactive token refresh failed', error);
                // Optionally handle logout here if needed, but AuthContext likely handles 401s
            }
        }, REFRESH_INTERVAL);

        return () => clearInterval(intervalId);
    }, [user, refreshTokens]);

    // Poll for agent inbox unread count
    useEffect(() => {
        if (!user) return;
        const fetchCount = async () => {
            try {
                const result = await apiClient.getActionRequestUnreadCount();
                setInboxUnreadCount(result.count || 0);
            } catch (e) {
                // silent - don't disrupt the UI for badge polling
            }
        };
        fetchCount();
        const id = setInterval(fetchCount, 10000);
        return () => clearInterval(id);
    }, [user]);

    const { addToast } = useToast();
    const userId = user?.id ?? null;
    const activeRole = (user?.role || '').toLowerCase();
    const originalRole = (user?.original_role || activeRole || '').toLowerCase();
    const availableRoles = useMemo(
        () => USER_ROLES.filter((role) => role && role !== originalRole),
        [originalRole]
    );
    const canViewAlerts = hasPermission('alerts:read') || hasPermission('devices:read');
    const canViewWifi = hasPermission('devices:read');
    const canViewWan = hasPermission('wan:read');
    const canViewUsers = hasPermission('users:read') || ['viewer', 'support'].includes(activeRole);
    const isAlertsSection = [PAGES.ALERTS, PAGES.CRASH_REPORT, PAGES.RESTARTS, PAGES.STATUS].includes(currentPage);
    const isUsersSection = [PAGES.USERS, PAGES.FCM_MANAGER, PAGES.USER_PREFERENCES, PAGES.MONITOR].includes(currentPage);
    const isWifiSection = [PAGES.WIFI, PAGES.APP_METRICS, PAGES.CH_UTIL, PAGES.TX_RX_RETRIES].includes(currentPage);

    const [isReportModalOpen, setReportModalOpen] = useState(false);
    const [isSwitchRoleModalOpen, setSwitchRoleModalOpen] = useState(false);
    const [devices, setDevices] = useState([]);
    const [devicesLoading, setDevicesLoading] = useState(false);
    const [devicesError, setDevicesError] = useState(null);
    const devicesLoadedRef = useRef(false);
    const [switchRoleSubmitting, setSwitchRoleSubmitting] = useState(false);
    const [revertRoleSubmitting, setRevertRoleSubmitting] = useState(false);
    const [apiVersion, setApiVersion] = useState(null);

    useEffect(() => {
        console.log("Checking API health...");
        apiClient.getApiVersion()
            .then(data => {
                console.log("API Health Check Passed:", data);
                setApiVersion(data.version);
            })
            .catch(err => console.error('Failed to fetch API version (Health Check Failed)', err));
    }, []);

    const {
        reasons: reportReasons,
        loading: reportReasonsLoading,
        error: reportReasonsError,
        refresh: refreshReportReasons,
    } = useReportReasons({ enabled: currentPage === PAGES.HOME || isReportModalOpen });

    const [problemAreas, setProblemAreas] = useState([]);
    const [problemAreasLoading, setProblemAreasLoading] = useState(false);
    const [problemAreasError, setProblemAreasError] = useState(null);

    const [problemServices, setProblemServices] = useState([]);
    const [problemServicesLoading, setProblemServicesLoading] = useState(false);
    const [problemServicesError, setProblemServicesError] = useState(null);

    const {
        reports: issueReports,
        loading: issueReportsLoading,
        error: issueReportsError,
        refresh: refreshIssueReports,
    } = useUserIssueReports({ enabled: currentPage === PAGES.HOME });

    const openIssueReports = useMemo(
        () => (issueReports || []).filter((report) => report?.is_open === true),
        [issueReports]
    );

    const loadProblemAreas = useCallback(async () => {
        setProblemAreasLoading(true);
        setProblemAreasError(null);
        try {
            const data = await apiClient.getUserProblemAreas();
            setProblemAreas(Array.isArray(data) ? data : []);
            return data;
        } catch (err) {
            setProblemAreas([]);
            setProblemAreasError(err?.message || 'Failed to load problem areas.');
            throw err;
        } finally {
            setProblemAreasLoading(false);
        }
    }, []);

    const loadProblemServices = useCallback(async () => {
        setProblemServicesLoading(true);
        setProblemServicesError(null);
        try {
            const data = await apiClient.getUserProblemServices();
            setProblemServices(Array.isArray(data) ? data : []);
            return data;
        } catch (err) {
            setProblemServices([]);
            setProblemServicesError(err?.message || 'Failed to load problem services.');
            throw err;
        } finally {
            setProblemServicesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!(currentPage === PAGES.HOME || isReportModalOpen)) {
            return;
        }
        loadProblemAreas().catch(() => { });
    }, [currentPage, isReportModalOpen, loadProblemAreas]);

    useEffect(() => {
        if (currentPage !== PAGES.HOME) {
            return;
        }
        loadProblemServices().catch(() => { });
    }, [currentPage, loadProblemServices]);

    const loadDevices = useCallback(async () => {
        setDevicesLoading(true);
        setDevicesError(null);
        try {
            const list = await apiClient.getDevices();
            const filtered = userId ? list.filter((device) => device.user_id === userId) : list;
            setDevices(filtered);
            return filtered;
        } catch (error) {
            const message = error?.message || 'Failed to load devices.';
            setDevicesError(message);
            setDevices([]);
            throw error;
        } finally {
            devicesLoadedRef.current = true;
            setDevicesLoading(false);
        }
    }, [userId]);

    useEffect(() => {
        if (!isReportModalOpen) {
            return;
        }
        if (!devicesLoadedRef.current) {
            loadDevices().catch(() => { });
        }
    }, [isReportModalOpen, loadDevices]);

    const reasonLookup = useMemo(() => {
        const map = new Map();
        reportReasons.forEach((reason) => {
            map.set(reason.id, reason.description);
        });
        return map;
    }, [reportReasons]);

    const problemAreaLookup = useMemo(() => {
        const map = new Map();
        problemAreas.forEach((area) => {
            map.set(area.id, area.type_description);
        });
        return map;
    }, [problemAreas]);

    const problemServiceLookup = useMemo(() => {
        const map = new Map();
        problemServices.forEach((service) => {
            map.set(service.id, service.detail);
        });
        return map;
    }, [problemServices]);

    useEffect(() => {
        if (!canViewAlerts) {
            setAlertsMenuOpen(false);
        }
    }, [canViewAlerts]);

    const deviceLookup = useMemo(() => {
        const map = new Map();
        devices.forEach((device) => {
            map.set(device.id, device);
        });
        return map;
    }, [devices]);

    const handleOpenReportModal = () => {
        devicesLoadedRef.current = false;
        setReportModalOpen(true);
    };

    const handleCloseReportModal = () => {
        setReportModalOpen(false);
    };

    const handleReloadDevices = useCallback(() => {
        devicesLoadedRef.current = false;
        loadDevices().catch(() => { });
    }, [loadDevices]);

    const handleOpenSwitchRoleModal = () => {
        setSwitchRoleModalOpen(true);
    };

    const handleCloseSwitchRoleModal = () => {
        if (!switchRoleSubmitting) {
            setSwitchRoleModalOpen(false);
        }
    };

    const handleSubmitIssueReport = useCallback(
        async (payload) => {
            try {
                await apiClient.createUserIssueReport(payload);
                addToast({
                    type: 'success',
                    title: 'Issue reported',
                    description: 'Thanks for letting us know. We will take a look.',
                });
                setReportModalOpen(false);
                await refreshIssueReports();
            } catch (error) {
                const message = error?.message || 'Failed to submit issue.';
                throw new Error(message);
            }
        },
        [addToast, refreshIssueReports]
    );

    const handleCloseIssueReport = useCallback(
        async (reportId) => {
            try {
                await apiClient.deleteUserIssueReport(reportId);
                addToast({
                    type: 'info',
                    title: 'Issue closed',
                    description: 'This issue has been marked as closed.',
                });
                await refreshIssueReports();
            } catch (error) {
                addToast({
                    type: 'error',
                    title: 'Unable to close issue',
                    description: error?.message || 'Please try again.',
                });
                throw error;
            }
        },
        [addToast, refreshIssueReports]
    );

    const handleSubmitSwitchRole = useCallback(
        async (role) => {
            setSwitchRoleSubmitting(true);
            try {
                await impersonateRole(role);
                addToast({
                    type: 'success',
                    title: 'Role switched',
                    description: `You are now acting as ${role}.`,
                });
                setSwitchRoleModalOpen(false);
            } catch (error) {
                const message = error?.message || 'Unable to switch role.';
                addToast({
                    type: 'error',
                    title: 'Unable to switch role',
                    description: message,
                });
                throw new Error(message);
            } finally {
                setSwitchRoleSubmitting(false);
            }
        },
        [addToast, impersonateRole]
    );

    const handleRevertRole = useCallback(async () => {
        setRevertRoleSubmitting(true);
        try {
            await revertRole();
            addToast({
                type: 'info',
                title: 'Role restored',
                description: 'You are now acting as admin again.',
            });
        } catch (error) {
            addToast({
                type: 'error',
                title: 'Unable to restore role',
                description: error?.message || 'Please try again later.',
            });
        } finally {
            setRevertRoleSubmitting(false);
        }
    }, [addToast, revertRole]);

    const handleIntervalSelect = useCallback((interval) => {
        setSelectedInterval(interval);
        if (interval !== 'custom') {
            setCustomRange({ start: '', end: '' });
        }
    }, []);

    const handleCustomRangeChange = useCallback((range) => {
        setCustomRange(range);
        if (range.start && range.end) {
            setSelectedInterval('custom');
        }
    }, []);

    const { wifiData, loading: dataLoading, error: dataError } = useWifiData(
        currentPage === PAGES.WIFI ? selectedDevice : null,
        selectedInterval,
        customRange
    );

    const intervalLabel = useMemo(() => {
        switch (selectedInterval) {
            case INTERVALS.TODAY:
                return 'Today';
            case INTERVALS.YESTERDAY:
                return 'Yesterday';
            case INTERVALS.WEEK:
                return 'Last Week';
            case INTERVALS.MONTH:
                return 'Last Month';
            case INTERVALS.CUSTOM:
                if (customRange.start && customRange.end) {
                    return `${customRange.start} → ${customRange.end}`;
                }
                return 'Custom range';
            default:
                return selectedInterval;
        }
    }, [selectedInterval, customRange.start, customRange.end]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setSettingsOpen(false);
            }
            if (alertsMenuRef.current && !alertsMenuRef.current.contains(event.target)) {
                setAlertsMenuOpen(false);
            }
            if (usersMenuRef.current && !usersMenuRef.current.contains(event.target)) {
                setUsersMenuOpen(false);
            }
            if (wifiMenuRef.current && !wifiMenuRef.current.contains(event.target)) {
                setWifiMenuOpen(false);
            }
            if (networkMenuRef.current && !networkMenuRef.current.contains(event.target)) {
                setNetworkMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const path = location.pathname.endsWith('/') && location.pathname !== '/'
            ? location.pathname.slice(0, -1)
            : location.pathname;

        if (path === '/wan' && currentPage !== PAGES.WAN) {
            setCurrentPage(PAGES.WAN);
        } else if (path === '/wifi' && currentPage !== PAGES.WIFI) {
            setCurrentPage(PAGES.WIFI);
        } else if (path === '/wifi/ch-util' && currentPage !== PAGES.CH_UTIL) {
            setCurrentPage(PAGES.CH_UTIL);
        } else if (path === '/wifi/tx-rx-retries' && currentPage !== PAGES.TX_RX_RETRIES) {
            setCurrentPage(PAGES.TX_RX_RETRIES);
        } else if (path === '/app-metrics' && currentPage !== PAGES.APP_METRICS) {
            setCurrentPage(PAGES.APP_METRICS);
        } else if (path === '/users' && currentPage !== PAGES.USERS) {
            setCurrentPage(PAGES.USERS);
        } else if (path === '/fcm-manager' && currentPage !== PAGES.FCM_MANAGER) {
            setCurrentPage(PAGES.FCM_MANAGER);
        } else if (path === '/user-preferences' && currentPage !== PAGES.USER_PREFERENCES) {
            setCurrentPage(PAGES.USER_PREFERENCES);
        } else if (path === '/preferences' && currentPage !== PAGES.PREFERENCES) {
            setCurrentPage(PAGES.PREFERENCES);
        } else if (path === '/alerts' && currentPage !== PAGES.ALERTS) {
            setCurrentPage(PAGES.ALERTS);
        } else if (path === '/crash-report' && currentPage !== PAGES.CRASH_REPORT) {
            setCurrentPage(PAGES.CRASH_REPORT);
        } else if (path === '/restarts' && currentPage !== PAGES.RESTARTS) {
            setCurrentPage(PAGES.RESTARTS);
        } else if (path === '/status' && currentPage !== PAGES.STATUS) {
            setCurrentPage(PAGES.STATUS);
        } else if (path === '/network-map' && currentPage !== PAGES.NETWORK_DIAGRAM) {
            setCurrentPage(PAGES.NETWORK_DIAGRAM);
        } else if (path === '/setup-wizard' && currentPage !== PAGES.SETUP_WIZARD) {
            setCurrentPage(PAGES.SETUP_WIZARD);
        } else if (path === '/monitor' && currentPage !== PAGES.MONITOR) {
            setCurrentPage(PAGES.MONITOR);
        } else if (path === '/diagnostics-ai' && currentPage !== PAGES.AI_AGENT) {
            setCurrentPage(PAGES.AI_AGENT);
        } else if (path === '/agent-inbox' && currentPage !== PAGES.AGENT_INBOX) {
            setCurrentPage(PAGES.AGENT_INBOX);
        } else if (
            path === '/' &&
            currentPage !== PAGES.HOME &&
            currentPage !== PAGES.DEVICES &&
            currentPage !== PAGES.DEPLOYMENTS
        ) {
            setCurrentPage(PAGES.HOME);
        }
    }, [currentPage, location.pathname]);

    const handleNavigation = (page, userId = null, deviceId = null) => {
        if (page === PAGES.USERS && !canViewUsers) {
            return;
        }
        if (page === PAGES.FCM_MANAGER && !hasPermission('users:read')) {
            return;
        }
        if (page === PAGES.USER_PREFERENCES && !canViewUsers) {
            return;
        }
        if ((page === PAGES.WIFI || page === PAGES.CH_UTIL || page === PAGES.TX_RX_RETRIES) && !canViewWifi) {
            return;
        }
        if (page === PAGES.APP_METRICS && !canViewWifi) {
            return;
        }
        if (page === PAGES.WAN && !canViewWan) {
            return;
        }
        if (page === PAGES.NETWORK_DIAGRAM && !canViewWifi) {
            return;
        }
        if (page === PAGES.ALERTS && !canViewAlerts) {
            return;
        }
        if ((page === PAGES.CRASH_REPORT || page === PAGES.RESTARTS || page === PAGES.STATUS) && !canViewAlerts) {
            return;
        }
        setSettingsOpen(false);
        setAlertsMenuOpen(false);
        setUsersMenuOpen(false);
        setWifiMenuOpen(false);
        setNetworkMenuOpen(false);
        setCustomRange({ start: '', end: '' });
        switch (page) {
            case PAGES.WAN:
                navigate('/wan');
                break;
            case PAGES.WIFI:
                navigate('/wifi');
                break;
            case PAGES.CH_UTIL:
                navigate('/wifi/ch-util');
                break;
            case PAGES.TX_RX_RETRIES:
                navigate('/wifi/tx-rx-retries');
                break;
            case PAGES.APP_METRICS:
                navigate('/app-metrics');
                break;
            case PAGES.NETWORK_DIAGRAM:
                navigate('/network-map');
                break;
            case PAGES.SETUP_WIZARD:
                navigate('/setup-wizard');
                break;
            case PAGES.MONITOR:
                navigate('/monitor');
                break;
            case PAGES.USERS:
                navigate('/users');
                break;
            case PAGES.FCM_MANAGER:
                navigate('/fcm-manager');
                break;
            case PAGES.USER_PREFERENCES:
                navigate('/user-preferences');
                break;
            case PAGES.PREFERENCES:
                navigate('/preferences');
                break;
            case PAGES.ALERTS:
                navigate('/alerts');
                break;
            case PAGES.CRASH_REPORT:
                navigate('/crash-report');
                break;
            case PAGES.RESTARTS:
                navigate('/restarts');
                break;
            case PAGES.STATUS:
                navigate('/status');
                break;
            case PAGES.AI_AGENT:
                navigate('/diagnostics-ai');
                break;
            case PAGES.AGENT_INBOX:
                navigate('/agent-inbox');
                break;
            case PAGES.HOME:
                navigate('/');
                break;
            default:
                navigate('/');
                break;
        }

        setCurrentPage(page);

        if (deviceId !== null && deviceId !== undefined) {
            setSelectedDevice(deviceId);
        }
    };

    const handleChangePassword = () => {
        setSettingsOpen(false);
        navigate('/change-password');
    };

    const handlePreferences = () => {
        handleNavigation(PAGES.PREFERENCES);
    };

    const handleLogout = () => {
        setSettingsOpen(false);
        logout();
    };

    const firstName = user?.firstname || user?.email || 'User';
    const userInitial = (user?.firstname?.[0] || user?.email?.[0] || 'U').toUpperCase();
    const displayName = user?.firstname && user?.lastname ? `${user.firstname} ${user.lastname}` : firstName;

    return (
        <div className="App">
            <div className="app-header">
                <div className="app-header__brand">
                    <img src="/logo.svg" alt="Buudii" className="app-logo app-logo--primary" />
                    <span className="app-title">Command Center</span>
                </div>
                <div className="app-header__actions">
                    <ThemeToggle />
                    {user ? (
                        <div className="app-header__user" ref={dropdownRef}>
                            <div className="app-header__avatar" aria-hidden="true">
                                {userInitial}
                            </div>
                            <div className="user-menu">
                                <button
                                    type="button"
                                    className="user-menu__toggle"
                                    onClick={() => setSettingsOpen((prev) => !prev)}
                                    aria-haspopup="menu"
                                    aria-expanded={settingsOpen}
                                >
                                    <span>{firstName}</span>
                                    <SettingsIcon size="18px" />
                                </button>
                                {settingsOpen ? (
                                    <div className="user-menu__panel" role="menu">
                                        <div className="user-menu__panel-header">
                                            <strong>{displayName}</strong>
                                            <span>{`Role: ${(activeRole || 'unknown').toUpperCase()}`}</span>
                                            {isImpersonating ? (
                                                <span>{`Impersonating ${activeRole}`}</span>
                                            ) : null}
                                        </div>
                                        {originalRole === 'admin' ? (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={handleOpenSwitchRoleModal}
                                                    role="menuitem"
                                                    disabled={switchRoleSubmitting || availableRoles.length === 0}
                                                >
                                                    {switchRoleSubmitting ? 'Switching role…' : 'Switch role'}
                                                </button>
                                                {isImpersonating ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleRevertRole}
                                                        role="menuitem"
                                                        disabled={revertRoleSubmitting}
                                                    >
                                                        {revertRoleSubmitting ? 'Restoring…' : 'Return to admin'}
                                                    </button>
                                                ) : null}
                                            </>
                                        ) : null}
                                        <button type="button" onClick={handleChangePassword} role="menuitem">
                                            Change Password
                                        </button>
                                        <button type="button" onClick={handlePreferences} role="menuitem">
                                            Preferences
                                        </button>
                                        {activeRole === 'admin' ? (
                                            <button
                                                type="button"
                                                onClick={() => handleNavigation(PAGES.MONITOR)}
                                                role="menuitem"
                                            >
                                                Monitor
                                            </button>
                                        ) : null}
                                        <button type="button" onClick={handleLogout} role="menuitem">
                                            Logout
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="nav-buttons">
                <button
                    type="button"
                    onClick={() => handleNavigation(PAGES.HOME)}
                    className={`nav-button ${currentPage === PAGES.HOME ? 'is-active' : ''}`}
                >
                    🏠 Home
                </button>
                <div className="nav-dropdown" ref={wifiMenuRef}>
                    <button
                        type="button"
                        onClick={() => setWifiMenuOpen((prev) => !prev)}
                        className={`nav-button nav-dropdown__toggle ${isWifiSection ? 'is-active' : ''}`}
                        aria-haspopup="menu"
                        aria-expanded={wifiMenuOpen}
                        style={{ display: canViewWifi ? 'flex' : 'none' }}
                    >
                        📶 Wifi
                    </button>
                    {wifiMenuOpen && canViewWifi ? (
                        <div className="user-menu__panel nav-dropdown__panel" role="menu">
                            <button
                                type="button"
                                className="nav-dropdown__panel-item"
                                onClick={() => handleNavigation(PAGES.WIFI)}
                                role="menuitem"
                            >
                                Analysis
                            </button>
                            <button
                                type="button"
                                className="nav-dropdown__panel-item"
                                onClick={() => handleNavigation(PAGES.CH_UTIL)}
                                role="menuitem"
                            >
                                Channel Util
                            </button>
                            {activeRole === 'admin' ? (
                                <button
                                    type="button"
                                    className="nav-dropdown__panel-item"
                                    onClick={() => handleNavigation(PAGES.TX_RX_RETRIES)}
                                    role="menuitem"
                                >
                                    Tx/Rx Retries
                                </button>
                            ) : null}
                            <button
                                type="button"
                                className="nav-dropdown__panel-item"
                                onClick={() => handleNavigation(PAGES.APP_METRICS)}
                                role="menuitem"
                            >
                                App Metrics
                            </button>
                        </div>
                    ) : null}
                </div>
                <div className="nav-dropdown" ref={networkMenuRef}>
                    <button
                        type="button"
                        onClick={() => setNetworkMenuOpen((prev) => !prev)}
                        className={`nav-button nav-dropdown__toggle ${currentPage === PAGES.NETWORK_DIAGRAM ? 'is-active' : ''}`}
                        aria-haspopup="menu"
                        aria-expanded={networkMenuOpen}
                        style={{ display: canViewWifi ? 'flex' : 'none' }}
                    >
                        🌐 Network
                    </button>
                    {networkMenuOpen && canViewWifi ? (
                        <div className="user-menu__panel nav-dropdown__panel" role="menu">
                            <button
                                type="button"
                                className="nav-dropdown__panel-item"
                                onClick={() => handleNavigation(PAGES.NETWORK_DIAGRAM)}
                                role="menuitem"
                            >
                                Network Map
                            </button>
                        </div>
                    ) : null}
                </div>
                <button
                    type="button"
                    onClick={() => handleNavigation(PAGES.WAN)}
                    className={`nav-button ${currentPage === PAGES.WAN ? 'is-active' : ''}`}
                    style={{ display: canViewWan ? 'inline-block' : 'none' }}
                >
                    🌐 WAN
                </button>
                <button
                    type="button"
                    onClick={() => handleNavigation(PAGES.AI_AGENT)}
                    className={`nav-button ${currentPage === PAGES.AI_AGENT ? 'is-active' : ''}`}
                    style={{ display: activeRole === 'admin' ? 'inline-block' : 'none', color: '#6366f1' }}
                >
                    🤖 Diagnostics AI
                </button>
                <button
                    type="button"
                    onClick={() => handleNavigation(PAGES.AGENT_INBOX)}
                    className={`nav-button ${currentPage === PAGES.AGENT_INBOX ? 'is-active' : ''}`}
                    style={{ display: activeRole === 'admin' ? 'inline-block' : 'none', color: '#6366f1' }}
                >
                    📥 Agent Inbox
                    {inboxUnreadCount > 0 && (
                        <span className="inbox-badge">{inboxUnreadCount}</span>
                    )}
                </button>
                <button
                    type="button"
                    onClick={() => handleNavigation(PAGES.SETUP_WIZARD)}
                    className={`nav-button ${currentPage === PAGES.SETUP_WIZARD ? 'is-active' : ''}`}
                >
                    🪄 Setup
                </button>

                {canViewAlerts ? (
                    <div className="nav-dropdown" ref={alertsMenuRef}>
                        <button
                            type="button"
                            onClick={() => setAlertsMenuOpen((prev) => !prev)}
                            className={`nav-button nav-dropdown__toggle ${isAlertsSection ? 'is-active' : ''}`}
                            aria-haspopup="menu"
                            aria-expanded={alertsMenuOpen}
                        >
                            🚨 Events
                        </button>
                        {alertsMenuOpen ? (
                            <div className="user-menu__panel nav-dropdown__panel" role="menu">
                                <button
                                    type="button"
                                    className="nav-dropdown__panel-item"
                                    onClick={() => handleNavigation(PAGES.ALERTS)}
                                    role="menuitem"
                                >
                                    Alerts
                                </button>
                                <button
                                    type="button"
                                    className="nav-dropdown__panel-item"
                                    onClick={() => handleNavigation(PAGES.CRASH_REPORT)}
                                    role="menuitem"
                                >
                                    Crash Report
                                </button>
                                <button
                                    type="button"
                                    className="nav-dropdown__panel-item"
                                    onClick={() => handleNavigation(PAGES.RESTARTS)}
                                    role="menuitem"
                                >
                                    Restarts
                                </button>
                                <button
                                    type="button"
                                    className="nav-dropdown__panel-item"
                                    onClick={() => handleNavigation(PAGES.STATUS)}
                                    role="menuitem"
                                >
                                    Status
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
                {canViewUsers ? (
                    <div className="nav-dropdown" ref={usersMenuRef}>
                        <button
                            type="button"
                            onClick={() => setUsersMenuOpen((prev) => !prev)}
                            className={`nav-button nav-dropdown__toggle ${isUsersSection ? 'is-active' : ''}`}
                            aria-haspopup="menu"
                            aria-expanded={usersMenuOpen}
                        >
                            {activeRole === 'viewer' ? '👤 My Account' : '👥 Users'}
                        </button>
                        {usersMenuOpen ? (
                            <div className="user-menu__panel nav-dropdown__panel" role="menu">
                                <button
                                    type="button"
                                    className="nav-dropdown__panel-item"
                                    onClick={() => handleNavigation(PAGES.USERS)}
                                    role="menuitem"
                                >
                                    {hasPermission('users:read') ? 'User Admin' : 'My Profile'}
                                </button>
                                {hasPermission('users:read') ? (
                                    <button
                                        type="button"
                                        className="nav-dropdown__panel-item"
                                        onClick={() => handleNavigation(PAGES.FCM_MANAGER)}
                                        role="menuitem"
                                    >
                                        FCM Admin
                                    </button>
                                ) : null}
                                <button
                                    type="button"
                                    className="nav-dropdown__panel-item"
                                    onClick={() => handleNavigation(PAGES.USER_PREFERENCES)}
                                    role="menuitem"
                                >
                                    User Preferences
                                </button>
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <Breadcrumbs
                currentPage={currentPage}
                selectedUser={null}
                selectedDevice={selectedDevice}
                onNavigate={handleNavigation}
            />

            {currentPage === PAGES.HOME && (
                <div className="home-content issue-reports-section">
                    <div className="issue-reports-header">
                        <h2 className="issue-reports-title">Reported issues</h2>
                        <button type="button" className="primary-btn" onClick={handleOpenReportModal}>
                            Report Issue
                        </button>
                    </div>
                    <IssueReportsList
                        reports={openIssueReports}
                        loading={issueReportsLoading}
                        error={issueReportsError}
                        onRetry={() => refreshIssueReports().catch(() => { })}
                        reasonLookup={reasonLookup}
                        deviceLookup={deviceLookup}
                        problemAreaLookup={problemAreaLookup}
                        problemServiceLookup={problemServiceLookup}
                        problemAreasLoading={problemAreasLoading}
                        problemAreasError={problemAreasError}
                        onRetryProblemAreas={() => loadProblemAreas().catch(() => { })}
                        problemServicesLoading={problemServicesLoading}
                        problemServicesError={problemServicesError}
                        onRetryProblemServices={() => loadProblemServices().catch(() => { })}
                        onCloseReport={(reportId) => handleCloseIssueReport(reportId)}
                        emptyMessage="no reported issues"
                    />
                </div>
            )}

            {currentPage === PAGES.USERS && canViewUsers && <PgUsersDashboard />}
            {currentPage === PAGES.FCM_MANAGER && hasPermission('users:read') && <FCMManager />}
            {currentPage === PAGES.USER_PREFERENCES && canViewUsers && <UserPreferences />}
            {currentPage === PAGES.PREFERENCES && <UserPreferences />}
            {currentPage === PAGES.APP_METRICS && <AppMetrics />}
            {currentPage === PAGES.SETUP_WIZARD && <SetupWizardPage />}
            {currentPage === PAGES.NETWORK_DIAGRAM && (
                <NetworkDiagram
                    selectedDevice={selectedDevice}
                    onDeviceChange={setSelectedDevice}
                    selectedInterval={selectedInterval}
                    customRange={customRange}
                    onIntervalChange={handleIntervalSelect}
                    onRangeChange={handleCustomRangeChange}
                />
            )}
            {currentPage === PAGES.MONITOR && <MonitorPage />}

            {currentPage === PAGES.WIFI && (
                <div className="dashboard-content">
                    <header style={{ marginBottom: '20px' }}>
                        <h2>Wifi Analysis</h2>
                        <p className="section-intro">
                            Inspect noise and interference computed for the device in the 2.4Ghz and 5Ghz bands.
                        </p>
                    </header>
                    <section className="data-controls">
                        <div className="data-controls__layout">
                            <div className="data-controls__device">
                                <div className="data-controls__title">Data analysis controls</div>
                                <DeviceDropdown selectedDevice={selectedDevice} onDeviceChange={setSelectedDevice} />
                            </div>
                            <span className="data-controls__divider" aria-hidden="true" />
                            <div className="data-controls__interval">
                                <div className="data-controls__interval-label">Data interval</div>
                                <IntervalButtons
                                    selectedInterval={selectedInterval}
                                    onIntervalChange={handleIntervalSelect}
                                    customRange={customRange}
                                    onCustomRangeChange={handleCustomRangeChange}
                                />
                            </div>
                        </div>
                        <div className="data-controls__actions">
                            <div className="view-buttons">
                                <button
                                    onClick={() => setCurrentView(VIEWS.PLOTS)}
                                    className={`view-button ${currentView === VIEWS.PLOTS ? 'active' : ''}`}
                                >
                                    📊 Charts
                                </button>
                                <button
                                    onClick={() => setCurrentView(VIEWS.TABLES)}
                                    className={`view-button ${currentView === VIEWS.TABLES ? 'active' : ''}`}
                                >
                                    📋 Tables
                                </button>
                            </div>
                        </div>
                    </section>

                    {selectedDevice ? (
                        <p className="selection-meta">
                            <span>Showing data for device</span>
                            <strong>{selectedDevice}</strong>
                            <span>Interval: {intervalLabel}</span>
                        </p>
                    ) : null}

                    <hr />

                    {dataLoading && (
                        <div className="loading-message">
                            <p>Loading WiFi data for {selectedDevice}...</p>
                        </div>
                    )}

                    {dataError && (
                        <div className="error-message">
                            <strong>Error loading data:</strong>
                            <div className="error-message__details">{dataError}</div>
                        </div>
                    )}

                    {!dataLoading && !dataError && selectedDevice && wifiData && (
                        <div className="data-section">
                            <Suspense fallback={<div>Rendering content...</div>}>
                                {currentView === VIEWS.PLOTS ? (
                                    <DevicePlot wifiData={wifiData} />
                                ) : (
                                    <TabularData wifiData={wifiData} />
                                )}
                            </Suspense>
                        </div>
                    )}

                </div>
            )}

            {currentPage === PAGES.WAN && (
                <WanDashboard
                    selectedDevice={selectedDevice}
                    onDeviceChange={setSelectedDevice}
                    selectedInterval={selectedInterval}
                    customRange={customRange}
                    onIntervalChange={handleIntervalSelect}
                    onRangeChange={handleCustomRangeChange}
                />
            )}

            {currentPage === PAGES.CH_UTIL && (
                <ChUtilPage
                    selectedDevice={selectedDevice}
                    onDeviceChange={setSelectedDevice}
                    selectedInterval={selectedInterval}
                    customRange={customRange}
                    onIntervalChange={handleIntervalSelect}
                    onRangeChange={handleCustomRangeChange}
                />
            )}

            {currentPage === PAGES.TX_RX_RETRIES && (
                <TxRxRetriesPage
                    selectedDevice={selectedDevice}
                    onDeviceChange={setSelectedDevice}
                    selectedInterval={selectedInterval}
                    customRange={customRange}
                    onIntervalChange={handleIntervalSelect}
                    onRangeChange={handleCustomRangeChange}
                />
            )}

            {currentPage === PAGES.ALERTS && canViewAlerts && (
                <AlertsDashboard
                    selectedDevice={selectedDevice}
                    onDeviceChange={setSelectedDevice}
                    selectedInterval={selectedInterval}
                    customRange={customRange}
                    onIntervalChange={handleIntervalSelect}
                    onRangeChange={handleCustomRangeChange}
                />
            )}

            {currentPage === PAGES.STATUS && canViewAlerts && (
                <StatusDashboard
                    selectedDevice={selectedDevice}
                    onDeviceChange={setSelectedDevice}
                    selectedInterval={selectedInterval}
                    customRange={customRange}
                    onIntervalChange={handleIntervalSelect}
                    onRangeChange={handleCustomRangeChange}
                />
            )}

            {currentPage === PAGES.CRASH_REPORT && canViewAlerts && (
                <CrashDashboard
                    selectedDevice={selectedDevice}
                    onDeviceChange={setSelectedDevice}
                    selectedInterval={selectedInterval}
                    customRange={customRange}
                    onIntervalChange={handleIntervalSelect}
                    onRangeChange={handleCustomRangeChange}
                />
            )}

            {currentPage === PAGES.RESTARTS && canViewAlerts && (
                <RestartsDashboard
                    selectedDevice={selectedDevice}
                    onDeviceChange={setSelectedDevice}
                    selectedInterval={selectedInterval}
                    customRange={customRange}
                    onIntervalChange={handleIntervalSelect}
                    onRangeChange={handleCustomRangeChange}
                />
            )}

            {[PAGES.ALERTS, PAGES.CRASH_REPORT, PAGES.RESTARTS, PAGES.STATUS].includes(currentPage) && !canViewAlerts && (
                <div className="dashboard-content">
                    <ErrorMessage title="Access denied" message="You do not have permission to view these events." />
                </div>
            )}

            {currentPage === PAGES.AI_AGENT && activeRole === 'admin' && (
                <AgentChat />
            )}

            {currentPage === PAGES.AGENT_INBOX && activeRole === 'admin' && (
                <AgentInbox onView={() => setInboxUnreadCount(0)} />
            )}

            <ReportIssueModal
                isOpen={isReportModalOpen}
                onClose={handleCloseReportModal}
                reasons={reportReasons}
                loadingReasons={reportReasonsLoading}
                reasonsError={reportReasonsError}
                onRefreshReasons={() => refreshReportReasons().catch(() => { })}
                devices={devices}
                loadingDevices={devicesLoading}
                devicesError={devicesError}
                onRefreshDevices={handleReloadDevices}
                onSubmit={handleSubmitIssueReport}
            />

            <SwitchRoleModal
                isOpen={isSwitchRoleModalOpen}
                onClose={handleCloseSwitchRoleModal}
                roles={availableRoles}
                currentRole={activeRole}
                originalRole={originalRole}
                onSubmit={handleSubmitSwitchRole}
                submitting={switchRoleSubmitting}
            />

            <footer className="app-footer">
                <img src="/fosco_labs_logo.png" alt="Fosco Labs" className="footer-logo" />
                <span>© Foscolabs 2025</span>
                <div className="footer-versions">
                    <span>Client: v{clientPackage.version}</span>
                    {apiVersion && <span>API: v{apiVersion}</span>}
                </div>
            </footer>
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <ToastProvider>
                <BrowserRouter>
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forbidden" element={<ForbiddenPage />} />
                        <Route
                            path="/"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/wifi"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/wifi/ch-util"
                            element={
                                <RequireAuth allowedRoles={['admin']}>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/wifi/tx-rx-retries"
                            element={
                                <RequireAuth allowedRoles={['admin']}>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/wan"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/alerts"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/crash-report"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/status"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/restarts"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/users"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/fcm-manager"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/user-preferences"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/preferences"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/change-password"
                            element={
                                <RequireAuth>
                                    <ChangePasswordPage />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/app-metrics"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/network-map"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/monitor"
                            element={
                                <RequireAuth roles={['admin', 'viewer', 'support']}>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/diagnostics-ai"
                            element={
                                <RequireAuth allowedRoles={['admin']}>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/agent-inbox"
                            element={
                                <RequireAuth allowedRoles={['admin']}>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route
                            path="/setup-wizard"
                            element={
                                <RequireAuth>
                                    <DashboardShell />
                                </RequireAuth>
                            }
                        />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
            </ToastProvider>
        </AuthProvider>
    );
}

export default App;